<template>
  <!-- Floating Bug Report Button -->
  <div class="bug-report-widget">
    <!-- Floating Tab/Button -->
    <Button 
      @click="visible = true"
      icon="pi pi-exclamation-triangle" 
      severity="secondary"
      class="bug-report-tab"
      v-tooltip.left="'Report a Bug'"
      rounded
      outlined
    />
    
    <!-- Bug Report Dialog -->
    <Dialog 
      v-model:visible="visible" 
      modal 
      header="Report a Bug" 
      :style="{ width: '500px' }"
      :closable="true"
    >
    <!-- View Issue List Button -->
    <div class="mb-3">
      <Button 
        @click="goToBugList"
        label="View Issue List" 
        icon="pi pi-list"
        text
        size="small"
        class="text-primary"
      />
    </div>
    
    <BugReportForm
        :title="bugReport.title"
        :category="bugReport.category"
        :description="bugReport.description"
        :submitting="submitting"
        :show-validation="showValidation"
        :include-context="true"
        :show-cancel="true"
        @update:title="bugReport.title = $event"
        @update:category="bugReport.category = $event"
        @update:description="bugReport.description = $event"
        @submit="submitBugReport"
        @cancel="closeDialog"
      />


    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import BugReportForm from './BugReportForm.vue'

// Composables
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

// Reactive data
const visible = ref(false)
const submitting = ref(false)
const showValidation = ref(false)

const goToBugList = () => {
  router.push('/issues')
}

// Bug report form data
const bugReport = ref({
  title: '',
  category: 'general',
  description: ''
})

// Computed
const currentRoute = computed(() => {
  return `${String(route.name)} (${route.path})`
})

// Methods
const submitBugReport = async (formData: { title: string, category: string, description: string }) => {
  if (!formData.title.trim()) {
    showValidation.value = true
    return
  }

  submitting.value = true
  try {
    const headers: any = {
      'Content-Type': 'application/json'
    }
    
    // Get CSRF token for POST request
    const csrfToken = await authStore.getCsrfToken()
    headers['X-CSRFToken'] = csrfToken

    // Add current page context to description
    const contextualDescription = formData.description 
      ? `${formData.description}\n-\nReported from: ${currentRoute.value}`
      : `Reported from: ${currentRoute.value}`

    const response = await fetch('/api/issues/', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        ...formData,
        description: contextualDescription
      })
    })

    if (response.ok) {
      toast.add({
        severity: 'success',
        summary: 'Bug Report Submitted',
        detail: 'Thank you! Your bug report has been submitted successfully.',
        life: 5000
      })
      closeDialog()
    } else {
      const error = await response.json()
      toast.add({
        severity: 'error',
        summary: 'Submission Failed',
        detail: error.detail || 'Failed to submit bug report. Please try again.',
        life: 5000
      })
    }
  } catch (error) {
    console.error('Failed to submit bug report:', error)
    toast.add({
      severity: 'error',
      summary: 'Network Error',
      detail: 'Failed to submit bug report. Please check your connection and try again.',
      life: 5000
    })
  } finally {
    submitting.value = false
  }
}

const closeDialog = () => {
  visible.value = false
  showValidation.value = false
  // Reset form after a brief delay to avoid visual glitches
  setTimeout(() => {
    bugReport.value = {
      title: '',
      category: 'general',
      description: ''
    }
  }, 300)
}
</script>

<style scoped>
.bug-report-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.bug-report-tab {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.bug-report-tab:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .bug-report-widget {
    bottom: 15px;
    right: 15px;
  }
}
</style>
